/**
 * @author NikolaV
 */
import { pointsToEN } from '../ol-utils';
import { FeatureMouseInteraction } from '../interaction/FeatureMouseInteraction';
import Feature, { FeatureLike } from 'ol/Feature';

import { Polygon as OlPolygon } from 'ol/geom';
import * as olStyle from 'ol/style';

const FEATURE_TYPE = 'model';

const stylesCache = {};

function createStyle(rgbStr: string) {
    rgbStr = rgbStr || '0,0,0';
    let rgb: any[] = rgbStr.split(',');
    if (rgb.length < 3) rgbStr = '0,0,0';

    if (stylesCache[rgbStr]) return stylesCache[rgbStr];

    if (rgbStr === '0,0,0') {
        // when model is black, default to blue
        rgb = [100, 100, 230];
    }

    const styles = {
        normal: new olStyle.Style({
            stroke: new olStyle.Stroke({
                color: [rgb[0], rgb[1], rgb[2], 0.6],
                width: 1.2,
            }),
            fill: new olStyle.Fill({
                color: [rgb[0], rgb[1], rgb[2], 0.2],
            }),
        }),

        selected: new olStyle.Style({
            stroke: new olStyle.Stroke({
                color: [0, 0, 0, 0.6],
                width: 1.2,
            }),
            fill: new olStyle.Fill({
                color: [rgb[0], rgb[1], rgb[2], 0.7],
            }),
        }),
    };

    stylesCache[rgbStr] = styles;
    return styles;
}

function styleFunction(feature: FeatureLike) {
    const data = feature.get('data');
    let rgbStr: string = null;
    if (data.color && data.color != '0,0,0') {
        rgbStr = data.color;
    } else if (data.isSolidPolylines) {
        rgbStr = '117,191,255';
    }
    const style = createStyle(rgbStr);
    return [feature.get('hover') ? style.selected : style.normal];
}

/*
 *  Main model factory, will also create gt points
 */
function makeFeatures(layer: IOutlineLayer, mouseInteraction?: FeatureMouseInteraction, structures?: IStructures[]): Feature[] {
    const polyFeatures: Feature[] = [];
    const polylinePolygonPaths: Coordinate[][] = [];

    layer.polylines.forEach(polyline => {
        if (polyline.solid) {
            const coords: Coordinate[] = pointsToEN(polyline.points);
            if (coords) {
                polylinePolygonPaths.push(coords);
            } else {
                console.warn('Skipping bad outline polyline: ', polyline);
                throw new Error();
            }
        } else {
            // non solid, a regular line (.gt, .sos)
            polyline.modelId = layer.id;
        }
    });

    layer.polygons.forEach(polygon => {
        const polyPaths: Coordinate[][] = [];
        const coords: Coordinate[] = pointsToEN(polygon.points);
        if (coords && coords.length > 0) {
            polyPaths.push(coords);
        }

        polygon.innerLoops.forEach(innerLoop => {
            const coords: Coordinate[] = pointsToEN(innerLoop);
            if (coords && coords.length > 0) {
                polyPaths.push(coords);
            }
        });

        if (polyPaths.length > 0 && polyPaths.some(path => path && path.length > 2)) {
            // Close the polygon loops
            for (const path of polyPaths) {
                if (path.length > 0) {
                    path.push(path[0]);
                }
            }

            const poly = new Feature({
                geometry: new OlPolygon(polyPaths),
            });

            const polySource: FeatureSource = {
                modelId: layer.id,
                modelName: layer.name,
            };
            poly.set('type', FEATURE_TYPE);
            poly.set('source', polySource);
            poly.set('data', { name: layer.name, props: polygon.props, color: polygon.color });
            poly.set('mouseInteraction', mouseInteraction);

            // !!! poly.setId(null) id needs to be left out, because server can return many layers with same id (from same model)
            // only unique ids are allowed in layerSource

            polyFeatures.push(poly);
        } else {
            try {
                console.log('layer ' + layer?.name + ' has 0 area polygon ' + polygon?.props?.name);
            } catch {}
        }
    });

    if (polylinePolygonPaths.length > 0) {
        // server doesn't return closed polys
        // so we need to copy the first coord to make sure first and last coord are the same
        // this fixes a problem where OL removes (some) points outside of viewport, and polys drawing gets messed up
        polylinePolygonPaths[0].push(polylinePolygonPaths[0][0]);

        const polylinePolygon = new Feature({
            geometry: new OlPolygon(polylinePolygonPaths),
        });
        const polylinePolygonSource: FeatureSource = {
            modelId: layer.id,
            modelName: layer.name,
        };

        const structProps = structures ? handleStructures({ structures }, layer, []) : [];

        polylinePolygon.set('type', FEATURE_TYPE);
        polylinePolygon.set('source', polylinePolygonSource);
        polylinePolygon.set('data', {
            name: layer.name,
            props: structures && !!structures[0] ? structProps[0]?.props : layer.props,
            color: layer.polylines[0].color,
            isSolidPolylines: true,
        });
        polylinePolygon.set('mouseInteraction', mouseInteraction);
        // polylinePolygon.setId(null) id needs to be left out, because server can return many layers with same id (from same model), only unique ids are allowed in layerSource
        polyFeatures.push(polylinePolygon);
    }

    return polyFeatures;
}

const handleStructures = (structures, layer, props) => {
    structures.structures.forEach(struct => {
        handleStructures(struct, layer, props);
    });
    if (structures.triangleMeshes) {
        for (const mesh of structures.triangleMeshes) {
            if (mesh === layer.props?.name) {
                structures.props.type = structures.type;
                props.push({ props: structures.props });
                break;
            }
        }
    }
    return props;
};

export const ModelOutlineFeatureFactory = {
    makeFeatures,
    styleFunction,
    createStyle,
    styles: stylesCache,
    FEATURE_TYPE,
};
